import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import * as api from './api';
import { setVerifyingToken, tokenStorage, userStorage, refreshStorage, refreshAccessToken, clearAuthStorage, setSessionNotice, getLastRefreshFailure, noticeFromDetail } from './api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback will exchange the session_id first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const savedUser = userStorage.get();
    if (savedUser) setUser(savedUser);

    const restore = async () => {
      // No access token (tab/app was closed) but a refresh token exists: silently re-authenticate
      const hadRefresh = !!refreshStorage.get();
      if (!tokenStorage.get() && hadRefresh) {
        const token = await refreshAccessToken();
        if (!token) {
          if (getLastRefreshFailure() === 'network' && refreshStorage.get()) {
            // Offline at startup: keep the cached user and retry on the next request/heartbeat
            return;
          }
          setSessionNotice(noticeFromDetail(getLastRefreshFailure()));
        }
      }
      if (!tokenStorage.get()) {
        clearAuthStorage();
        setUser(null);
        return;
      }
      setVerifyingToken(true);
      try {
        const res = await api.getMe();
        setUser(res.data);
        userStorage.set(res.data);
      } catch (error) {
        clearAuthStorage();
        setUser(null);
      } finally {
        setVerifyingToken(false);
      }
    };

    restore().finally(() => setLoading(false));
  }, []);

  // Keep the server session alive while the app is open/visible; resume after returning to the app
  useEffect(() => {
    if (!user) return undefined;
    const ping = () => {
      if (document.visibilityState !== 'visible') return;
      if (!tokenStorage.get() && !refreshStorage.get()) return;
      api.getMe().catch(() => {});
    };
    const interval = setInterval(ping, 5 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user]);

  const persistSession = useCallback((data) => {
    const { token, refresh_token: refreshToken, user: userData } = data;
    tokenStorage.set(token);
    if (refreshToken) refreshStorage.set(refreshToken);
    userStorage.set(userData);
    setUser(userData);
    return userData;
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.login({ email, password });
    return persistSession(response.data);
  }, [persistSession]);

  const register = useCallback(async (email, password, name, referralCode) => {
    const response = await api.register({ email, password, name, referral_code: referralCode });
    return persistSession(response.data);
  }, [persistSession]);

  const loginWithTokens = useCallback((data) => persistSession(data), [persistSession]);

  const logout = useCallback(() => {
    // Revoke the server-side session (best effort), then clear local storage
    const token = tokenStorage.get();
    if (token) api.logoutSession(token).catch(() => {});
    clearAuthStorage();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.getMe();
      setUser(res.data);
      userStorage.set(res.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({ 
    user, 
    login, 
    register, 
    loginWithTokens,
    logout, 
    loading,
    refreshUser,
    isAuthenticated: !!user 
  }), [user, login, register, loginWithTokens, logout, loading, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
